import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

import { PhoneNumberInput } from '@/components/PhoneNumberInput.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { emailSchema, phoneNumberSchema } from '@/lib/validation-schemas.ts';

const formId = 'insurance-follow-up-form';
const formSchema = z.object({
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
});

export type FormData = z.infer<typeof formSchema>;

interface Props {
  onSubmit: (data: FormData) => void;
}

export function InsuranceFollowUpForm({ onSubmit }: Props) {
  const form = useForm<FormData>({ resolver: zodResolver(formSchema) });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
      <div className="space-y-8 sm:space-y-12">
        <FieldGroup>
          <div className="space-y-7">
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-phoneNumber`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Phone number</FieldLabel>
                    <PhoneNumberInput {...field} id={inputId} aria-invalid={fieldState.invalid} />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-email`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Email</FieldLabel>
                    <Input
                      {...field}
                      id={inputId}
                      type="email"
                      aria-invalid={fieldState.invalid}
                      placeholder="example@email.com"
                      inputMode="email"
                    />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />
          </div>
        </FieldGroup>

        <Button type="submit" className="w-full">
          Confirm
        </Button>
      </div>
    </form>
  );
}
